"use client";

// 2026-05-26: Polymarket geoblock UX banner.
//
// Mounts globally but only renders when:
//   1. The geoblock check has resolved and `blocked=true`, AND
//   2. The user is on a page where they could ACT on Polymarket
//      markets (browse + trade). We deliberately skip non-trading
//      surfaces — /portfolio, /copytrading, /traders, /settings —
//      because the banner there would be noise: a US user reviewing
//      their existing portfolio doesn't need a "can't trade" warning
//      on every page reload.
//
// Compliance background: Polymarket docs ask builders to surface this
// "before they attempt to trade". The hard block lives on Polymarket's
// CLOB; this banner exists so the user sees the message at page load
// instead of after signing a Privy transaction.

import { usePolymarketGeoblock } from "@/hooks/usePolymarketGeoblock";
import { useCurrentSource } from "@/hooks/useCurrentSource";
import { usePathname } from "next/navigation";
import { ShieldAlert } from "lucide-react";

// Pages where the banner is meaningful — the user is browsing markets
// and could attempt a trade from this page. Anything else (portfolio,
// copytrading, traders directory, settings, etc.) is muted.
const TRADING_ROUTES_REGEX = /^\/(polymarket|markets|market|event)(\/|$|\?)/;

export default function PolymarketGeoblockBanner() {
  const geo = usePolymarketGeoblock();
  const source = useCurrentSource();
  const pathname = usePathname() || "";

  // Only render when:
  //   - We have a definitive blocked=true (no flashing while loading)
  //   - The current page is a Polymarket trading surface (not portfolio)
  //   - The active source is polymarket (a /polymarket/* URL is implicit;
  //     unprefixed /markets|/market|/event also resolve to polymarket)
  //   - The geoblock check didn't error out (we fail-open in that case)
  if (geo.loading || !geo.blocked || geo.error) return null;
  if (source !== "polymarket") return null;
  if (!TRADING_ROUTES_REGEX.test(pathname)) return null;

  const regionLabel = [geo.country, geo.region]
    .filter((s): s is string => !!s && s.length > 0)
    .join(" / ");

  return (
    // z-[400] sits above LiveTicker (260) / sidebar (250) and below the
    // trading modal stack (9999) — same pattern as TradingDisabledBanner.
    <div className="fixed bottom-0 left-0 right-0 z-[400] pointer-events-none">
      <div className="bg-red-900/85 backdrop-blur-sm pointer-events-auto">
        <div className="w-full px-4 py-2 flex items-center justify-center gap-2.5 text-center">
          <ShieldAlert className="w-4 h-4 text-red-300 flex-shrink-0" />
          <p className="text-sm font-medium text-red-100">
            Polymarket trading is not available in your region
            {regionLabel ? ` (${regionLabel})` : ""}. You can browse
            and close existing positions, but new orders will be
            rejected.{" "}
            <a
              href="https://docs.polymarket.com/api-reference/geoblock"
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-200 hover:text-white underline decoration-red-300/60 hover:decoration-white whitespace-nowrap"
            >
              Learn more
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
